import {
  CANDIDATE_STATUSES,
  CANDIDATE_STATUS_LABELS_INTERNAL,
  CANDIDATE_STATUS_TRANSITIONS,
  type CandidateStatus,
  type CuratorStatusTarget,
} from '@remote-medium/shared';
import { Button, RadioGroup, RadioGroupItem } from '@remote-medium/ui';
import { useEffect, useState } from 'react';

import { Card, SectionHeader } from '../../../lib/ui.tsx';

const HINTS: Record<CandidateStatus, string> = {
  new: 'Waiting for screening. Not ranked yet.',
  screened: 'Reviewed, not matchable yet.',
  active: 'In the pool. Ranked against every open role each night.',
  paused: 'Out of the pool until you reactivate.',
  placed: 'Hired through an introduction.',
  withdrawn: 'Out of the pool for good.',
};

export function StatusCard({
  status,
  introduced,
  busy,
  onChange,
}: {
  status: CandidateStatus;
  /** Whether any match has had an introduction sent (gates "Placed"). */
  introduced: boolean;
  busy: boolean;
  onChange: (next: CuratorStatusTarget) => void;
}) {
  const [picked, setPicked] = useState<CandidateStatus>(status);
  useEffect(() => setPicked(status), [status]);

  const targets = CANDIDATE_STATUS_TRANSITIONS[status];
  const options = CANDIDATE_STATUSES.filter(
    (s) => s === status || (targets as readonly string[]).includes(s),
  );

  return (
    <Card>
      <SectionHeader title="Status" />
      <RadioGroup
        aria-label="Candidate status"
        value={picked}
        onValueChange={(v) => setPicked(v as CandidateStatus)}
        className="gap-4"
      >
        {options.map((s) => {
          const blocked = s === 'placed' && s !== status && !introduced;
          const id = `status-${s}`;
          return (
            <div key={s} className="flex items-start gap-3">
              <RadioGroupItem id={id} value={s} disabled={blocked} className="mt-0.5" />
              <label
                htmlFor={id}
                className={blocked ? 'flex flex-col opacity-60' : 'flex cursor-pointer flex-col'}
              >
                <span className="text-sm font-medium">{CANDIDATE_STATUS_LABELS_INTERNAL[s]}</span>
                <span className="text-xs text-muted-foreground">
                  {HINTS[s]}
                  {blocked && ' Available once one is sent.'}
                </span>
              </label>
            </div>
          );
        })}
      </RadioGroup>
      <div>
        <Button
          variant="outline"
          size="sm"
          disabled={busy || picked === status}
          onClick={() => onChange(picked as CuratorStatusTarget)}
        >
          {busy ? 'Updating…' : 'Update status'}
        </Button>
      </div>
    </Card>
  );
}
